// Download with progress, size limits and SHA-256 verification; verified bytes are cached in Cache Storage
// keyed by (repo, sha256) with the commit kept as a query (so unchanged files are reused across commits).
import { sha256Hex } from '../../shared/hash.ts';
import { RtwError } from './errors.ts';
import type { FetchFn } from './github.ts';

export interface ByteCache {
  get(key: string): Promise<Uint8Array | null>;
  put(key: string, bytes: Uint8Array): Promise<void>;
}

export const CACHE_NAME = 'rtw-files-v1';

/** Cache Storage implementation. Keys are paths on our own origin that the server never serves. */
export function cacheStorageCache(origin = location.origin): ByteCache | null {
  if (typeof caches === 'undefined') return null;
  const url = (key: string) => `${origin}/__rtw_cache/${key}`;
  return {
    async get(key) {
      try {
        const c = await caches.open(CACHE_NAME);
        const r = await c.match(url(key), { ignoreSearch: true });
        return r ? new Uint8Array(await r.arrayBuffer()) : null;
      } catch { return null; }
    },
    async put(key, bytes) {
      try {
        const c = await caches.open(CACHE_NAME);
        await c.put(url(key), new Response(bytes as Uint8Array<ArrayBuffer>, { headers: { 'content-type': 'application/octet-stream' } }));
      } catch { /* quota or private mode: still playable, just not cached */ }
    },
  };
}

export function memoryCache(): ByteCache & { map: Map<string, Uint8Array> } {
  const map = new Map<string, Uint8Array>();
  const strip = (k: string) => k.replace(/\?.*$/, '');
  return {
    map,
    async get(k) { return map.get(strip(k)) ?? null; },
    async put(k, b) { map.set(strip(k), b); },
  };
}

export interface FetchOpts {
  fetchFn?: FetchFn;
  maxBytes: number;
  expectedSize?: number;
  onProgress?: (loaded: number, total: number | null) => void;
  signal?: AbortSignal;
}

export async function download(url: string, opts: FetchOpts): Promise<Uint8Array> {
  const fetchFn = opts.fetchFn ?? fetch;
  let res: Response;
  try {
    res = await fetchFn(url, { credentials: 'omit', referrerPolicy: 'no-referrer', signal: opts.signal });
  } catch (e) {
    throw new RtwError('network', 'Download failed. Check your connection.', `${url}: ${String(e)}`);
  }
  if (res.status === 404) throw new RtwError('not-found', 'A file listed by the game is missing from its repository.', `${url}: HTTP 404`);
  if (!res.ok) throw new RtwError('network', 'Download failed.', `${url}: HTTP ${res.status}`);
  const lenHeader = Number(res.headers.get('content-length') ?? NaN);
  const expected = opts.expectedSize;
  const wrongSize = (got: number | string) => new RtwError('size', 'A game file has a different size than its manifest says, so it was not run.',
    `${url}: ${got} bytes, manifest says ${expected}`);
  // A Content-Length that disagrees with the manifest is refused before reading (compressed responses report the
  // encoded length, so only an unencoded one counts).
  if (expected !== undefined && Number.isFinite(lenHeader) && !res.headers.get('content-encoding') && lenHeader !== expected) throw wrongSize(lenHeader);
  const total = expected ?? (Number.isFinite(lenHeader) ? lenHeader : null);
  if (total !== null && total > opts.maxBytes) throw new RtwError('too-big', 'A game file is larger than the site allows.', `${url}: ${total} > ${opts.maxBytes} bytes`);
  const limit = Math.min(opts.maxBytes, expected ?? Infinity);
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  if (!res.body) {
    const b = new Uint8Array(await res.arrayBuffer());
    chunks.push(b); loaded = b.length;
  } else {
    const reader = res.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      loaded += value.length;
      if (loaded > limit) {
        await reader.cancel();
        if (expected !== undefined && loaded <= opts.maxBytes) throw wrongSize(`more than ${expected}`);
        throw new RtwError('too-big', 'A game file is larger than the site allows.', `${url}: more than ${opts.maxBytes} bytes`);
      }
      chunks.push(value);
      opts.onProgress?.(loaded, total);
    }
  }
  if (expected !== undefined && loaded !== expected) throw wrongSize(loaded);
  if (loaded > opts.maxBytes) throw new RtwError('too-big', 'A game file is larger than the site allows.', `${url}: ${loaded} > ${opts.maxBytes} bytes`);
  const out = new Uint8Array(loaded);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.length; }
  opts.onProgress?.(loaded, total ?? loaded);
  return out;
}

export interface VerifiedFile { path: string; sha256: string; size: number }

/** Get one file at a pinned commit: from the cache if present (re-verified), else downloaded, length-checked, then hashed. */
export async function getVerified(
  file: VerifiedFile, ctx: { url: string; repoKey: string; commit: string; cache: ByteCache | null; maxBytes: number;
    fetchFn?: FetchFn; onProgress?: (loaded: number, total: number | null) => void; signal?: AbortSignal },
): Promise<{ bytes: Uint8Array; fromCache: boolean }> {
  const key = `${ctx.repoKey}/${file.sha256}?commit=${ctx.commit}`;
  const cached = await ctx.cache?.get(key);
  if (cached && cached.length === file.size && (await sha256Hex(cached)) === file.sha256) {
    ctx.onProgress?.(cached.length, cached.length);
    return { bytes: cached, fromCache: true };
  }
  const bytes = await download(ctx.url, { fetchFn: ctx.fetchFn, maxBytes: ctx.maxBytes, expectedSize: file.size, onProgress: ctx.onProgress, signal: ctx.signal });
  const got = await sha256Hex(bytes);
  if (got !== file.sha256) {
    throw new RtwError('hash', `"${file.path}" does not match the hash in the game's manifest, so it was not run.`,
      `${ctx.url}\nexpected sha256 ${file.sha256}\nreceived sha256 ${got} (${bytes.length} bytes)`);
  }
  await ctx.cache?.put(key, bytes);
  return { bytes, fromCache: false };
}
